import { particles } from 'app/contracts/Particles';
import { DepartmentType } from 'app/data/Enums';
import { constrain } from 'utils/math';
import { HomeField } from 'app/views/home/webgl/particles/fields/HomeField';
import HomeFieldConfig from 'app/views/home/webgl/particles/config/HomeFieldConfig';
import TransitionParticles from 'app/views/home/webgl/particles/departments/TransitionParticles';
import HomeZonesConfig from 'app/views/home/config/HomeZonesConfig';
import { Events } from 'core/events/Events';
import { Simple } from 'utils/analytics';
import type { DepartmentScene } from 'app/views/home/webgl/webgl.types';

/**
 * The department scenes the switcher swaps between (keyed by scene id, from
 * HomeZonesConfig progress zones) plus the always-on transition-particles
 * processor, which is not a DepartmentScene.
 */
type SwitcherProcessors = {
	spaces: DepartmentScene;
	screens: DepartmentScene;
	systems: DepartmentScene;
	icon: DepartmentScene;
	transitions: TransitionParticles;
};

//Handle progress and switching between 3 main scenes at top level
export default class DepartmentSwitcher {
	private _sceneProgress: number = 0.5;
	private _transitionParticleCount: number;

	private _transitionShares: any = {
		screens: 0,
		icon: 100,
		spaces: 0
	};
	private _activeScene: string = 'icon';
	private _active: boolean = false;
	private _transitionsProcessor: TransitionParticles;
	private _sceneStartCounts = {
		icon: 0,
		systems: 0,
		screens: 0,
		spaces: 0
	};

	constructor(
		private _homeField: HomeField,
		private _sceneProps: particles.SceneProps,
		private _processors: SwitcherProcessors = null
	) {
		this._transitionParticleCount = HomeFieldConfig.transitionParticles;
		this._transitionsProcessor = this._processors.transitions;
	}

	public setIntialState(section: string): void {
		this._activeScene = section;

		this._transitionShares = {
			screens: 0,
			icon: 0,
			spaces: 0
		};

		this._transitionShares[section] = this._transitionParticleCount;
	}

	public tick() {
		if (!this._active) return;

		this._sceneProgress = this._sceneProps.progress.ratio;
		this._updateSceneTransition(this._sceneProgress);
		this._checkSwitchScene();
		this._updateTransitionShares();
	}

	/** On main scene on icon/screens/spaces */
	public startMainScene(scene: string) {
		this._active = true;
		this._activeScene = scene;
	}

	/** When coming back from work then set as type */
	public fromWorkToHome(department: DepartmentType) {
		this._active = true;
		this._activeScene = department;
		this._transitionShares = {
			screens: 0,
			icon: 0,
			spaces: 0
		};
		this._transitionShares[department] = 100;
	}

	//PRIVATE
	//----------------------------------------------------------------------------------------------
	//----------------------------------------------------------------------------------------------
	//----------------------------------------------------------------------------------------------
	//----------------------------------------------------------------------------------------------

	private _checkSwitchScene(): void {
		let currentScene = this._getSceneFromProgress();

		if (currentScene !== null && this._activeScene != currentScene) {
			this._switchScene(currentScene);
		}
	}

	private _getSceneFromProgress(): string {
		return HomeZonesConfig.getParticleSceneFromProgress(this._sceneProgress);
	}

	private _switchScene(newScene: string) {
		console.log('newScene', newScene);
		this._sceneStartCounts[newScene]++;

		Simple.event('start_home_scene', { scene: newScene, scene_count: this._sceneStartCounts[newScene] });

		this._homeField.setActiveSceneProcessor(this._processors[newScene]);
		this._activeScene = newScene;
		Events.dispatch('CHANGE_HOME_SCENE', { scene: newScene });
	}

	/**
	 * While user is tragging in the transition zone(s) then
	 * Progress will be -1 when sceens
	 * Progress will be 0 when home
	 * Progress will be +1 when spaaces
	 * This value should be used to infer what
	 */
	private _updateSceneTransition(progress: number): void {
		//Work out actual scene progress based on transition progress---------------------------------------
		//Spaces is 0-1
		if (progress < 0) {
			this._sceneProps.progress.spaces = 0;
		} else {
			let start = 0.0;
			let end = 0.7;
			let range = end - start;
			this._sceneProps.progress.spaces = constrain((progress - start) / range, 0, 1);
		}

		let rp = constrain(progress * 1.6, -1, 1);
		this._sceneProps.progress.home = constrain(1 - Math.abs(rp), 0, 1);

		//Screens needs to be 0 or over, and 1 at -1 progress:
		if (progress > 0) {
			this._sceneProps.progress.screens = 0;
		} else {
			let start = 0.0;
			let end = 0.7;
			let range = end - start;
			this._sceneProps.progress.screens = constrain((-1 * progress - start) / range, 0, 1);
		}
	}

	private _updateTransitionShares(): void {
		let iconBuffer = 0.05;
		let sideShare = 0.2;
		let shares = { spaces: 0, icon: 0, screens: 0 };
		let shareCounts = { spaces: 0, icon: 0, screens: 0 };

		if (this._sceneProgress > 0.5 - iconBuffer && this._sceneProgress < 0.5 + iconBuffer) {
			shares.icon = 1;
			shareCounts.icon = this._transitionParticleCount;
		} else if (this._sceneProgress < 0.5) {
			shares.icon = (this._sceneProgress - sideShare) / (0.5 - iconBuffer - sideShare);
			shares.screens = 1 - shares.icon;
			shareCounts.icon = Math.round(shares.icon * this._transitionParticleCount);
			shareCounts.screens = this._transitionParticleCount - shareCounts.icon;
		} else {
			let min = 0.5 + iconBuffer;
			let max = min + sideShare;
			shares.spaces = Math.min(1, (this._sceneProgress - min) / (max - min));
			shares.icon = 1 - shares.spaces;

			shareCounts.spaces = Math.round(shares.spaces * this._transitionParticleCount);
			shareCounts.icon = this._transitionParticleCount - shareCounts.spaces;
		}

		//Now work out real ration
		let hasChanges =
			shareCounts.screens != this._transitionShares.screens ||
			shareCounts.icon != this._transitionShares.icon ||
			shareCounts.spaces != this._transitionShares.spaces;

		if (hasChanges) {
			this._transitionShares = shareCounts;
			this._transitionsProcessor.setShares(this._transitionShares);
		}
	}

}
